import { Component, inject } from '@angular/core';
import {
    DxButtonComponent,
    DxDataGridComponent,
    DxFormComponent,
    DxTemplateDirective,
} from 'devextreme-angular';
import {
    DxiColumnComponent,
    DxiItemComponent,
    DxoColumnChooserComponent,
    DxoExportComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoLoadPanelComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoPositionComponent,
    DxoScrollingComponent,
    DxoSearchComponent,
    DxoSearchPanelComponent,
    DxoSelectionComponent,
    DxoSortingComponent,
    DxoToolbarComponent,
} from 'devextreme-angular/ui/nested';
import { FilterPanelComponent } from '../../../components/library/filter-panel/filter-panel.component';
import {
    FilterConfig,
    GridPageComponent,
} from '../../common/grid-page.component';
import {
    IncidentLocation,
    IncidentCategory,
    IncidentReportGridViewDto,
    IncidentStatus,
    IncidentType,
    InjuryType,
} from '@services';
import EnumUtils from '../../../shared/enum-utils';
import { DATE_TIME_FORMAT, YES_NO_OPTIONS } from '../../../shared/constants';
import { UserAvatarEmailComponent } from '../../../components/utils/user-avatar-email/user-avatar-email.component';
import { CapitalCasePipe } from '../../../pipes/capital-case.pipe';
import { DatePipe, NgClass } from '@angular/common';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { Router } from '@angular/router';
import { IncidentLocationPipe } from '../../../pipes/incident-location.pipe';
import { IncidentCategoryPipe } from '../../../pipes/incident-category.pipe';
import { IncidentTypePipe } from '../../../pipes/incident-type.pipe';
import { InjuryTypePipe } from '../../../pipes/injury-type.pipe';

@Component({
    selector: 'app-incident-report',
    imports: [
        DxDataGridComponent,
        DxoScrollingComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoLoadPanelComponent,
        DxoPositionComponent,
        DxoSortingComponent,
        DxoSearchComponent,
        DxoHeaderFilterComponent,
        DxoFilterRowComponent,
        DxoColumnChooserComponent,
        DxoSearchPanelComponent,
        DxoSelectionComponent,
        DxoExportComponent,
        DxoToolbarComponent,
        DxiItemComponent,
        DxButtonComponent,
        DxiColumnComponent,
        DxTemplateDirective,
        UserAvatarEmailComponent,
        CapitalCasePipe,
        DatePipe,
        NgClass,
        FilterPanelComponent,
        DxFormComponent,
        IncidentCategoryPipe,
        IncidentLocationPipe,
    ],
    providers: [IncidentLocationPipe, IncidentCategoryPipe, IncidentTypePipe, InjuryTypePipe],
    templateUrl: './incident-reports.component.html',
})
export class IncidentReportComponent extends GridPageComponent<IncidentReportGridViewDto> {
    private router = inject(Router);
    private incidentLocationPipe = inject(IncidentLocationPipe);
    private incidentCategoryPipe = inject(IncidentCategoryPipe);
    private incidentTypePipe = inject(IncidentTypePipe);
    private injuryTypePipe = inject(InjuryTypePipe);

    dataSource = this.dataSources.incidentReports();
    protected override exportName: string = 'Incident Reports';
    protected offset: number = 100;
    protected readonly dateTimeFormat = DATE_TIME_FORMAT;
    protected readonly yesNoOptions = YES_NO_OPTIONS;

    categoryOptions = EnumUtils.getEnumOptions(IncidentCategory, (v) => this.incidentCategoryPipe.transform(v));
    locationOptions = EnumUtils.getEnumOptions(IncidentLocation, (v) => this.incidentLocationPipe.transform(v));
    statusOptions = EnumUtils.getEnumOptions(IncidentStatus);
    typeOptions = EnumUtils.getEnumOptions(IncidentType, (v) => this.incidentTypePipe.transform(v));
    injuryTypeOptions = EnumUtils.getEnumOptions(InjuryType, (v) => this.injuryTypePipe.transform(v));

    createdByFilter = this.userNameOrEmailFilter('createdBy');
    lastModifiedByFilter = this.userNameOrEmailFilter('lastModifiedBy');

    protected override filterConfig: FilterConfig[] = [
        { field: 'number', op: 'contains' },
        {
            field: 'employee.name',
            op: 'contains',
            customFilter: this.nameOrEmailFilter,
        },
        { field: 'category', op: '=' },
        { field: 'location', op: '=' },
        { field: 'incidentDate', op: '>=' },
        { field: 'created', op: '>=' },
        {
            field: 'createdBy.name',
            op: 'contains',
            customFilter: this.createdByFilter,
        },
        { field: 'lastModified', op: '>=' },
        {
            field: 'lastModifiedBy.name',
            op: 'contains',
            customFilter: this.lastModifiedByFilter,
        },
        { field: 'type', op: '=' },
        { field: 'injuryType', op: '=' },
        { field: 'shiftCode', op: 'contains' },
        { field: 'humanResourcesContacted', op: '=' },
        { field: 'outsideMedical', op: '=' },
        { field: 'emsCalled', op: '=' },
        { field: 'firstAidAdministered', op: '=' },
        { field: 'lostTime', op: '=' },
    ];

    constructor() {
        super();
        this.init(IncidentReportGridViewDto);
    }

    employeeFilter = (filterValue: any, selectedFilterOperation: string) => {
        if (!filterValue) {
            return null;
        }

        return [
            [
                'externalName',
                selectedFilterOperation || 'contains',
                filterValue,
            ],
            'or',
            [
                'employee.name',
                selectedFilterOperation || 'contains',
                filterValue,
            ],
            'or',
            [
                'employee.email',
                selectedFilterOperation || 'contains',
                filterValue,
            ],
        ];
    };

    protected getBadgeClass(status: IncidentStatus): string {
        return EnumUtils.getIncidentStatusBadgeClass(status);
    }

    addIncidentReport = async () => {
        await this.router.navigate(['/incident-report/create']);
    };

    async onRowDblClick(e: DxDataGridTypes.RowDblClickEvent) {
        await this.router.navigate([`/incident-report/edit/${e.data.id}`]);
    }
}
